import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Sparkles, Trash2 } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { AppLayout, PageHeader } from '../components/layout/AppLayout'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { useConfirm } from '../components/ui/ConfirmDialog'
import { Dialog } from '../components/ui/Dialog'
import { Empty, Skeleton } from '../components/ui/Empty'
import { Input, Label, Textarea } from '../components/ui/Input'
import { apiClient } from '../lib/api'

type Module = {
  id: string
  code: string
  name: string
  description: string | null
  defaultPriceEur: string | null
  defaultHours: string | null
  tags: string[]
  features: string[]
  updatedAt: string
}

type ListResult = { results: Module[]; total: number }

export function ProposalModulesPage() {
  const qc = useQueryClient()
  const confirm = useConfirm()
  const [editing, setEditing] = useState<Module | null>(null)
  const [creating, setCreating] = useState(false)

  const modulesQ = useQuery({
    queryKey: ['proposal-modules'],
    queryFn: () => apiClient.get<ListResult>('/proposal-modules'),
  })

  const del = useMutation({
    mutationFn: (id: string) => apiClient.del(`/proposal-modules/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['proposal-modules'] }),
  })

  return (
    <AppLayout>
      <PageHeader
        title="Catalogo moduli"
        description={`${modulesQ.data?.total ?? 0} moduli riusabili nelle proposte`}
        action={
          <Button onClick={() => setCreating(true)}>
            <Plus className="size-4" /> Nuovo modulo
          </Button>
        }
      />

      {modulesQ.isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholder
            <Card key={i} className="p-4">
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-5 w-40" />
            </Card>
          ))}
        </div>
      ) : (modulesQ.data?.results.length ?? 0) === 0 ? (
        <Card className="p-8">
          <Empty
            icon={Sparkles}
            title="Nessun modulo nel catalogo"
            description="I moduli rappresentano voci riusabili nei preventivi (es. Setup auth + RBAC, Migrazione dati, Multilingua DE). Inseriscili qui per riutilizzarli nelle proposte future."
            action={
              <Button onClick={() => setCreating(true)}>
                <Plus className="size-4" /> Crea primo modulo
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {modulesQ.data?.results.map((m) => (
            <Card key={m.id} className="p-4 group cursor-pointer" onClick={() => setEditing(m)}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <span className="font-mono text-xs text-cyan-300">{m.code}</span>
                <button
                  type="button"
                  onClick={async (e) => {
                    e.stopPropagation()
                    const ok = await confirm({
                      title: `Eliminare modulo ${m.code}?`,
                      message: `"${m.name}" non sarà più disponibile nel catalogo. I PRV che lo referenziano via moduleRefId resteranno validi (i dati sono embedded).`,
                      confirmLabel: 'Elimina',
                      tone: 'danger',
                    })
                    if (ok) del.mutate(m.id)
                  }}
                  className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-pink-300 p-1 rounded"
                  aria-label="Elimina"
                >
                  <Trash2 className="size-3.5" />
                </button>
              </div>
              <div className="font-display font-semibold text-zinc-100 mb-1">{m.name}</div>
              {m.description && (
                <p className="text-xs text-zinc-400 line-clamp-2 mb-3">{m.description}</p>
              )}
              <div className="flex items-center gap-3 text-xs">
                {m.defaultPriceEur && (
                  <span className="font-mono text-emerald-300">
                    €{' '}
                    {Number(m.defaultPriceEur).toLocaleString('it-IT', {
                      minimumFractionDigits: 2,
                    })}
                  </span>
                )}
                {m.defaultHours && <span className="text-zinc-500">{Number(m.defaultHours)}h</span>}
              </div>
              {m.tags?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {m.tags.slice(0, 4).map((t) => (
                    <Badge key={t} variant="muted">
                      {t}
                    </Badge>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {(creating || editing) && (
        <ModuleEditDialog
          module={editing}
          onClose={() => {
            setCreating(false)
            setEditing(null)
          }}
        />
      )}
    </AppLayout>
  )
}

function ModuleEditDialog({
  module,
  onClose,
}: {
  module: Module | null
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [code, setCode] = useState(module?.code ?? '')
  const [name, setName] = useState(module?.name ?? '')
  const [description, setDescription] = useState(module?.description ?? '')
  const [defaultPriceEur, setDefaultPriceEur] = useState(module?.defaultPriceEur ?? '')
  const [defaultHours, setDefaultHours] = useState(module?.defaultHours ?? '')
  const [tagsStr, setTagsStr] = useState((module?.tags ?? []).join(', '))
  const [featuresStr, setFeaturesStr] = useState((module?.features ?? []).join('\n'))

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => {
      if (module) return apiClient.patch(`/proposal-modules/${module.id}`, body)
      return apiClient.post('/proposal-modules', body)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['proposal-modules'] })
      onClose()
    },
  })

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    save.mutate({
      code,
      name,
      description: description || undefined,
      defaultPriceEur: defaultPriceEur ? Number(defaultPriceEur) : undefined,
      defaultHours: defaultHours ? Number(defaultHours) : undefined,
      tags: tagsStr
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      features: featuresStr
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean),
    })
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={module ? `Modifica ${module.code}` : 'Nuovo modulo'}
      size="md"
    >
      <form onSubmit={onSubmit} className="p-5 space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label htmlFor="m-code">Code</Label>
            <Input
              id="m-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              placeholder="M-AUTH"
            />
          </div>
          <div className="col-span-2">
            <Label htmlFor="m-name">Nome</Label>
            <Input
              id="m-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Setup autenticazione + RBAC"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="m-desc">Descrizione</Label>
          <Textarea
            id="m-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="m-price">Prezzo default (€)</Label>
            <Input
              id="m-price"
              type="number"
              step="0.01"
              value={defaultPriceEur}
              onChange={(e) => setDefaultPriceEur(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="m-hours">Ore default</Label>
            <Input
              id="m-hours"
              type="number"
              step="0.5"
              value={defaultHours}
              onChange={(e) => setDefaultHours(e.target.value)}
            />
          </div>
        </div>
        <div>
          <Label htmlFor="m-tags">Tag (comma-separated)</Label>
          <Input
            id="m-tags"
            value={tagsStr}
            onChange={(e) => setTagsStr(e.target.value)}
            placeholder="backend, auth, security"
          />
        </div>
        <div>
          <Label htmlFor="m-features">Feature (una per riga)</Label>
          <Textarea
            id="m-features"
            value={featuresStr}
            onChange={(e) => setFeaturesStr(e.target.value)}
            rows={4}
            placeholder="Login email + password&#10;Reset password via email&#10;Ruoli admin/user/viewer"
          />
        </div>
        {save.isError && (
          <div className="text-sm text-pink-300 rounded-md bg-pink-500/10 border border-pink-500/30 px-3 py-2">
            Errore: {String(save.error)}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button type="submit" disabled={save.isPending || !code || !name}>
            {save.isPending ? 'Salvataggio…' : module ? 'Aggiorna' : 'Crea modulo'}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}
